"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getDailyResults = getDailyResults;
exports.aggregateDailyResults = aggregateDailyResults;
const openapi_1 = require("@pentacode/openapi");
const utils_1 = require("../utils");
const issues_1 = require("../issues");
const model_1 = require("../model");
const vacation_1 = require("../vacation");
const payroll_1 = require("../payroll");
const time_result_1 = require("./time-result");
const nominals_1 = require("./nominals");
const balances_1 = require("./balances");
const rpc_1 = require("../api/rpc");
function getDailyResults(company, employee, entries, { from, to }, previousResults) {
    // !!! This Function also potentially mutates the input TimeEntries and updates their results if not set
    var _a;
    const dailyResults = [];
    let date = from;
    let lastResult = previousResults[previousResults.length - 1];
    // time results are not used in this function, so we can call it before results are updated
    const issues = (0, issues_1.getShiftIssues)(employee, company, entries, { from, to });
    while (date < to) {
        const dayEntries = entries.filter((e) => e.date === date);
        const workEntries = dayEntries.filter((e) => e.type === model_1.TimeEntryType.Work);
        const reset = dayEntries.find((e) => e.type === model_1.TimeEntryType.ResetLedgers);
        const allResults = [...previousResults, ...dailyResults];
        const lastDayOfPreviousMonth = (0, utils_1.dateAdd)((0, utils_1.getRange)(date, "month").from, { days: -1 });
        const averagesResult = allResults.find((res) => res.date === lastDayOfPreviousMonth);
        const contract = employee.getContractForDate(date);
        const results = {
            work: (0, time_result_1.makeEmptyTimeResult)(),
            workPlanned: (0, time_result_1.makeEmptyTimeResult)(),
            absences: (0, time_result_1.makeEmptyTimeResult)(),
            adjustments: (0, time_result_1.makeEmptyTimeResult)(),
            total: (0, time_result_1.makeEmptyTimeResult)(),
        };
        let vacationDays = 0;
        let vacationAdjustments = 0;
        if (contract) {
            for (const entry of dayEntries) {
                if (!entry.result) {
                    entry.result = (0, time_result_1.getTimeResult)(company, employee, entry, (averagesResult === null || averagesResult === void 0 ? void 0 : averagesResult.averages["13weeks"]) || (0, time_result_1.makeEmptyTimeResult)(), workEntries.filter((e) => e.startFinal && e.endFinal).length, "final");
                }
                if (!entry.resultPlanned) {
                    entry.resultPlanned = (0, time_result_1.getTimeResult)(company, employee, entry, (averagesResult === null || averagesResult === void 0 ? void 0 : averagesResult.averages["13weeks"]) || (0, time_result_1.makeEmptyTimeResult)(), workEntries.length, "planned");
                }
                if (!entry.resultMaxTotalCosts) {
                    const { totalCosts } = (0, time_result_1.getTimeResult)(company, employee, entry, (averagesResult === null || averagesResult === void 0 ? void 0 : averagesResult.averages["13weeks"]) || (0, time_result_1.makeEmptyTimeResult)(), workEntries.length, "max");
                    entry.resultMaxTotalCosts = totalCosts;
                }
            }
            vacationDays = dayEntries
                .filter((e) => e.type === model_1.TimeEntryType.Vacation)
                .reduce((total, e) => { var _a; return (0, openapi_1.add)(total, (_a = e.days) !== null && _a !== void 0 ? _a : 1); }, 0);
            vacationAdjustments = dayEntries
                .filter((e) => e.type === model_1.TimeEntryType.VacationAdjustment)
                .reduce((total, e) => { var _a; return (0, openapi_1.add)(total, (_a = e.days) !== null && _a !== void 0 ? _a : 1); }, 0);
            results.work = (0, time_result_1.getTotalResults)(workEntries.map((e) => e.result).filter(Boolean));
            results.workPlanned = (0, time_result_1.getTotalResults)(workEntries.map((e) => e.resultPlanned).filter(Boolean));
            results.absences = (0, time_result_1.getTotalResults)(dayEntries
                .filter((e) => [
                model_1.TimeEntryType.Sick,
                model_1.TimeEntryType.Vacation,
                model_1.TimeEntryType.Sick,
                model_1.TimeEntryType.SickInKUG,
                model_1.TimeEntryType.ChildSick,
                model_1.TimeEntryType.CompDay,
            ].includes(e.type))
                .map((e) => e.result)
                .filter(Boolean));
            results.adjustments = (0, time_result_1.getTotalResults)(dayEntries
                .filter((e) => [model_1.TimeEntryType.HourAdjustment].includes(e.type))
                .map((e) => e.result)
                .filter(Boolean));
            results.total = (0, time_result_1.getTotalResults)([results.work, results.absences, results.adjustments]);
        }
        const getTimeBalance = (_13weekAverage, planned) => {
            var _a, _b, _c, _d;
            const carry = (planned ? (_a = lastResult === null || lastResult === void 0 ? void 0 : lastResult.timeBalancePlanned) === null || _a === void 0 ? void 0 : _a.balance : lastResult === null || lastResult === void 0 ? void 0 : lastResult.timeBalance.balance) || 0;
            const nominal = (0, nominals_1.getNominalTime)(company, employee, { from: date, to: (0, utils_1.dateAdd)(date, { days: 1 }) }, 
            //we go day by day here, so we can use the last 13 weeks average
            (0, nominals_1.createAbsenceHoursGetter)([{ date, averages: { "13weeks": _13weekAverage } }]));
            const work = planned ? ((_b = results.workPlanned) === null || _b === void 0 ? void 0 : _b.base.duration) || 0 : results.work.base.duration;
            const absences = results.absences.base.duration;
            const adjustments = results.adjustments.base.duration;
            const actual = (0, openapi_1.sum)(work, absences, adjustments);
            const difference = (0, openapi_1.subtract)(actual, nominal);
            const base = (_c = reset === null || reset === void 0 ? void 0 : reset.hours) !== null && _c !== void 0 ? _c : carry;
            const balance = (0, openapi_1.add)(base, difference);
            return {
                reset: (_d = reset === null || reset === void 0 ? void 0 : reset.hours) !== null && _d !== void 0 ? _d : undefined,
                nominal,
                work,
                adjustments,
                absences,
                actual,
                difference,
                carry,
                balance,
            };
        };
        const getVacationBalance = (timeBalance) => {
            var _a, _b;
            const contract = employee.getContractForDate(date);
            const carry = (lastResult === null || lastResult === void 0 ? void 0 : lastResult.vacationBalance.balance) || 0;
            const base = (_a = reset === null || reset === void 0 ? void 0 : reset.days) !== null && _a !== void 0 ? _a : carry;
            const vacationFactor = ((contract === null || contract === void 0 ? void 0 : contract.vacationIncrement) === model_1.VacationIncrement.Hourly && timeBalance.nominal
                ? timeBalance.actual / timeBalance.nominal
                : 1);
            let nominal = (0, vacation_1.calcVacationEntitlement)(employee, { from: date, to: (0, utils_1.dateAdd)(date, { days: 1 }) }, company.settings);
            nominal = (0, openapi_1.multiplyWithFactor)(nominal, vacationFactor);
            const actual = (0, openapi_1.add)(vacationDays, vacationAdjustments);
            const difference = (0, openapi_1.subtract)(nominal, actual);
            const balance = (0, openapi_1.add)(base, difference);
            return {
                reset: (_b = reset === null || reset === void 0 ? void 0 : reset.days) !== null && _b !== void 0 ? _b : undefined,
                nominal,
                taken: vacationDays,
                adjustments: vacationAdjustments,
                actual,
                difference,
                carry,
                balance,
            };
        };
        const getBonusesBalance = () => {
            var _a, _b;
            const taxed = results.total.bonuses
                .filter((b) => !b.taxFree)
                .reduce((total, b) => (0, openapi_1.add)(total, b.wages), 0);
            const untaxed = results.total.bonuses
                .filter((b) => b.taxFree)
                .reduce((total, b) => (0, openapi_1.add)(total, b.wages), 0);
            const actual = company.settings.includeTaxedBonusesInBalance ? (0, openapi_1.add)(taxed, untaxed) : untaxed;
            if (!(contract === null || contract === void 0 ? void 0 : contract.enableSFNLedger)) {
                return {
                    taxed,
                    untaxed,
                    actual,
                };
            }
            const nominal = (0, nominals_1.getNominalBonus)(employee, { from: date, to: (0, utils_1.dateAdd)(date, { days: 1 }) });
            const difference = (0, openapi_1.subtract)(actual, nominal);
            // Bonuses balance is always reset with the beginning of a new year
            const carry = date.endsWith("01-01") ? 0 : (lastResult === null || lastResult === void 0 ? void 0 : lastResult.bonusesBalance.balance) || 0;
            const base = (_a = reset === null || reset === void 0 ? void 0 : reset.resetBonus) !== null && _a !== void 0 ? _a : carry;
            const balance = (0, openapi_1.add)(base, difference);
            return {
                reset: (_b = reset === null || reset === void 0 ? void 0 : reset.resetBonus) !== null && _b !== void 0 ? _b : undefined,
                nominal,
                taxed,
                untaxed,
                actual,
                difference,
                carry,
                balance,
            };
        };
        let _13weekAverage = (lastResult === null || lastResult === void 0 ? void 0 : lastResult.averages["13weeks"]) || (0, time_result_1.makeEmptyTimeResult)();
        // For historical reasons, we calculate a new 13 week average for each month
        // and use the same average for the whole month instead of calculating a new
        // (more accurate) value for each day
        if ((0, utils_1.dateAdd)(date, { days: 1 }).endsWith("-01")) {
            // The previous daily results act as the basis for calcuating the average
            const allResults = [...previousResults, ...dailyResults];
            // We start calculating from the last work day
            const lastWorkDate = results.work.base.duration
                ? date
                : ((_a = [...allResults].reverse().find((r) => r.results.work.days)) === null || _a === void 0 ? void 0 : _a.date) || date;
            const to = (0, utils_1.dateAdd)(lastWorkDate, { days: 1 });
            // Averages are calculated from the last 13 weeks
            // (calculating backwards from the last work day)
            const from = (0, utils_1.dateAdd)(to, { weeks: -13 });
            const averagesInput = allResults.filter((r) => r.date >= from && r.date < to);
            // Only work results are considered
            const workResults = [...averagesInput.map((e) => e.results.work), results.work];
            _13weekAverage = (0, time_result_1.getDailyAverage)(workResults);
        }
        const timeBalance = getTimeBalance(_13weekAverage, false);
        const result = new rpc_1.DailyResultsDTO({
            employeeId: employee.id,
            date,
            averages: {
                "13weeks": _13weekAverage,
            },
            results,
            timeBalance,
            timeBalancePlanned: getTimeBalance(_13weekAverage, true),
            vacationBalance: getVacationBalance(timeBalance),
            bonusesBalance: getBonusesBalance(),
            issues: issues.filter((i) => i.date === date),
        });
        // If this is the last date of the payroll period, check for payroll issues
        if (new Date((0, utils_1.dateAdd)(date, { days: 1 })).getDate() === company.settings.startPayrollPeriod) {
            result.issues.push(...(0, issues_1.getPayrollIssues)(company, employee, entries, (0, payroll_1.getPayrollPeriod)(date, company.settings.startPayrollPeriod)));
        }
        dailyResults.push(result);
        lastResult = result;
        date = (0, utils_1.dateAdd)(date, { days: 1 });
    }
    return dailyResults;
}
/** @deprecated Use `timeBalanceFromDailyResults`, `vacationBalanceFromDailyResults`, `bonusesBalanceFromDailyResults` instead */
function aggregateDailyResults(results, roundValues = true, projected = false) {
    const firstResult = results[0];
    const today = (0, utils_1.toDateString)(new Date());
    const firstTimeBalance = (projected && (firstResult === null || firstResult === void 0 ? void 0 : firstResult.date) >= today && (firstResult === null || firstResult === void 0 ? void 0 : firstResult.timeBalancePlanned)) || (firstResult === null || firstResult === void 0 ? void 0 : firstResult.timeBalance);
    const balances = results.reduce((total, result) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const timeBalance = (projected && result.date >= today && result.timeBalancePlanned) || result.timeBalance;
        return {
            ...total,
            time: {
                ...total.time,
                carry: (_a = total.time.carry) !== null && _a !== void 0 ? _a : timeBalance.carry,
                actual: (0, openapi_1.add)(total.time.actual, timeBalance.actual),
                nominal: (0, openapi_1.add)(total.time.nominal, timeBalance.nominal),
                difference: (0, openapi_1.add)(total.time.difference, timeBalance.difference),
                work: (0, openapi_1.add)(total.time.work, timeBalance.work),
                absences: (0, openapi_1.add)(total.time.absences, timeBalance.absences),
                adjustments: (0, openapi_1.add)(total.time.adjustments, timeBalance.adjustments),
                balance: (_b = timeBalance.balance) !== null && _b !== void 0 ? _b : total.time.balance,
            },
            vacation: {
                ...total.vacation,
                carry: (_c = total.vacation.carry) !== null && _c !== void 0 ? _c : result.vacationBalance.carry,
                taken: (0, openapi_1.add)(total.vacation.taken, result.vacationBalance.taken),
                adjustments: (0, openapi_1.add)(total.vacation.adjustments, result.vacationBalance.adjustments),
                actual: (0, openapi_1.add)(total.vacation.actual, result.vacationBalance.actual),
                nominal: (0, openapi_1.add)(total.vacation.nominal, result.vacationBalance.nominal),
                difference: (0, openapi_1.add)(total.vacation.difference, result.vacationBalance.difference),
                balance: (_d = result.vacationBalance.balance) !== null && _d !== void 0 ? _d : total.vacation.balance,
            },
            bonuses: {
                ...total.bonuses,
                carry: (_e = total.bonuses.carry) !== null && _e !== void 0 ? _e : result.bonusesBalance.carry,
                taxed: (0, openapi_1.add)(total.bonuses.taxed, result.bonusesBalance.taxed),
                untaxed: (0, openapi_1.add)(total.bonuses.untaxed, result.bonusesBalance.untaxed),
                actual: (0, openapi_1.add)(total.bonuses.actual, result.bonusesBalance.actual),
                nominal: typeof total.bonuses.nominal === "number" && typeof result.bonusesBalance.nominal === "number"
                    ? (0, openapi_1.add)(total.bonuses.nominal, result.bonusesBalance.nominal)
                    : ((_f = total.bonuses.nominal) !== null && _f !== void 0 ? _f : result.bonusesBalance.nominal),
                difference: typeof total.bonuses.difference === "number" &&
                    typeof result.bonusesBalance.difference === "number"
                    ? (0, openapi_1.add)(total.bonuses.difference, result.bonusesBalance.difference)
                    : ((_g = total.bonuses.difference) !== null && _g !== void 0 ? _g : result.bonusesBalance.difference),
                balance: (_h = result.bonusesBalance.balance) !== null && _h !== void 0 ? _h : total.bonuses.balance,
            },
        };
    }, {
        time: {
            carry: (firstTimeBalance === null || firstTimeBalance === void 0 ? void 0 : firstTimeBalance.carry) || 0,
            work: 0,
            absences: 0,
            adjustments: 0,
            actual: 0,
            nominal: 0,
            difference: 0,
            balance: (firstTimeBalance === null || firstTimeBalance === void 0 ? void 0 : firstTimeBalance.balance) || 0,
            reset: firstTimeBalance === null || firstTimeBalance === void 0 ? void 0 : firstTimeBalance.reset,
        },
        vacation: {
            carry: (firstResult === null || firstResult === void 0 ? void 0 : firstResult.vacationBalance.carry) || 0,
            adjustments: 0,
            taken: 0,
            actual: 0,
            nominal: 0,
            difference: 0,
            balance: (firstResult === null || firstResult === void 0 ? void 0 : firstResult.vacationBalance.balance) || 0,
            reset: firstResult === null || firstResult === void 0 ? void 0 : firstResult.vacationBalance.reset,
        },
        bonuses: {
            carry: firstResult === null || firstResult === void 0 ? void 0 : firstResult.bonusesBalance.carry,
            taxed: 0,
            untaxed: 0,
            actual: 0,
            balance: firstResult === null || firstResult === void 0 ? void 0 : firstResult.bonusesBalance.balance,
            reset: firstResult === null || firstResult === void 0 ? void 0 : firstResult.bonusesBalance.reset,
        },
    });
    return roundValues ? (0, balances_1.roundBalances)(balances) : balances;
}
//# sourceMappingURL=daily-results.js.map